/**
 * Zone 4: Starlit Night Decorator
 *
 * Handles decoration for the Starlit Night zone (Zone 4)
 * Includes 8 decoration types: 5 top-side + 3 underside
 *
 * Top decorations: twinkling-star, crescent-moon, shooting-star, star-dust, night-moth
 * Underside decorations: icicle, water-drops, mini-waterfall
 *
 * PHASE 7C: Converted from Graphics to Sprites
 */

import Phaser from 'phaser';
import { PlatformDecoratorBase, DecorationConfig } from './PlatformDecoratorBase';
import { DecorationTextureGenerator } from '../DecorationTextureGenerator'; // PHASE 7C

export class Zone4Decorator extends PlatformDecoratorBase {
  protected getDecorations(): DecorationConfig[] {
    return [
      // Top-side decorations
      {
        type: 'twinkling-star',
        chance: 0.25,
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'crescent-moon',
        chance: 0.08,
        minWidth: 70,
        placement: 'top',
      },
      {
        type: 'shooting-star',
        chance: 0.12,
        minWidth: 60,
        placement: 'top',
      },
      {
        type: 'star-dust',
        chance: 0.2,
        minWidth: 50,
        placement: 'top',
      },
      {
        type: 'night-moth',
        chance: 0.15,
        minWidth: 45,
        placement: 'top',
      },
      // Underside decorations
      {
        type: 'icicle',
        chance: 0.45,
        minWidth: 40,
        placement: 'underside',
      },
      {
        type: 'water-drops',
        chance: 0.35,
        minWidth: 50,
        placement: 'underside',
      },
      {
        type: 'mini-waterfall',
        chance: 0.2,
        minWidth: 80,
        placement: 'underside',
      },
    ];
  }

  /**
   * Add a specific decoration to the platform
   * PHASE 7C: CONVERTED TO SPRITES (was Graphics)
   */
  protected addDecoration(
    platformContainer: Phaser.GameObjects.Container,
    decorationType: string,
    _placement: 'top' | 'underside',
    width: number,
    height: number
  ): void {
    // PHASE 7C: Create sprite from pre-generated texture
    const textureKey = DecorationTextureGenerator.getTextureKey(decorationType);
    const sprite = this.scene.add.sprite(0, 0, textureKey);
    sprite.setOrigin(0.5, 0.5);
    platformContainer.add(sprite);

    const position = this.calculateDecorationPosition(decorationType, width, height);
    sprite.setPosition(position.x, position.y);

    const animationType = this.getAnimationType(decorationType);
    if (animationType) {
      this.animatedElements.push({
        sprite,
        type: animationType,
        animationPhase: this.random.nextFloat(0, Math.PI * 2),
        animationSpeed: this.getAnimationSpeed(animationType),
      });
    }
  }

  private calculateDecorationPosition(
    type: string,
    width: number,
    height: number
  ): { x: number; y: number } {
    // CRITICAL FIX: Only use platform-dependent positioning (constant offsets in texture)
    switch (type) {
      case 'twinkling-star':
        return {
          x: this.random.nextFloat(-width * 0.4, width * 0.4),
          y: -height / 2, // Texture has the random -10 to -25 offset
        };
      case 'crescent-moon':
        return {
          x: this.random.nextFloat(-width * 0.15, width * 0.15),
          y: -height / 2, // Texture has the -15 offset
        };
      case 'shooting-star':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the random -12 to -22 offset
        };
      case 'star-dust':
        return {
          x: this.random.nextFloat(-width * 0.35, width * 0.35),
          y: -height / 2, // Texture has the random -8 to -18 offset
        };
      case 'night-moth':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the random -12 to -20 offset
        };
      case 'icicle':
        return {
          x: this.random.nextFloat(-width * 0.4, width * 0.4),
          y: height / 2,
        };
      case 'water-drops':
        return { x: 0, y: height / 2 }; // drawWaterDrops handles multiple drops internally
      case 'mini-waterfall':
        return {
          x: this.random.nextFloat(-width * 0.2, width * 0.2),
          y: height / 2,
        };
      default:
        return { x: 0, y: 0 };
    }
  }

  private getAnimationType(type: string): string | null {
    switch (type) {
      case 'twinkling-star':
        return 'star-twinkle';
      default:
        return null;
    }
  }

  private getAnimationSpeed(type: string): number {
    switch (type) {
      case 'star-twinkle':
        return 0.08;
      default:
        return 0.05;
    }
  }
}
